import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@/components/ui';
import { registerSchema, type RegisterFormValues } from '../schemas/auth.schemas';
import { useRegister } from '../hooks/useRegister';
import { PasswordToggle } from './PasswordToggle';

interface RegisterFormProps {
  /** Called with the registered email once the auth server has created the account. */
  onRegistered: (email: string) => void;
}

export function RegisterForm({ onRegistered }: RegisterFormProps) {
  const { t } = useTranslation();
  const register_ = useRegister();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { nume: '', prenume: '', email: '', phone: '', password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit((values) => {
    register_.mutate(
      {
        nume: values.nume,
        prenume: values.prenume,
        email: values.email,
        phone: values.phone,
        password: values.password,
        confirmPassword: values.confirmPassword,
      },
      {
        onSuccess: () => onRegistered(values.email),
        onError: (err) => {
          for (const [field, message] of Object.entries(err.fieldErrors ?? {})) {
            const key = field === 'confirm_password' ? 'confirmPassword' : field;
            if (['nume', 'prenume', 'email', 'phone', 'password', 'confirmPassword'].includes(key)) {
              setError(key as keyof RegisterFormValues, { message });
            }
          }
        },
      },
    );
  });

  const formError =
    register_.isError && !register_.error.fieldErrors
      ? t(register_.error.message, { defaultValue: register_.error.message })
      : null;

  const fieldError = (name: keyof RegisterFormValues) =>
    errors[name] ? t(errors[name]?.message ?? '') : undefined;

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      {formError && (
        <div role="alert" className="rounded-input bg-danger-light px-3.5 py-2.5 text-sm text-danger">
          {formError}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <TextField label={t('register.nume')} autoComplete="family-name" error={fieldError('nume')} {...register('nume')} />
        <TextField label={t('register.prenume')} autoComplete="given-name" error={fieldError('prenume')} {...register('prenume')} />
      </div>

      <TextField label={t('auth.login.email')} type="email" autoComplete="email" error={fieldError('email')} {...register('email')} />
      <TextField label={t('register.phone')} type="tel" autoComplete="tel" error={fieldError('phone')} {...register('phone')} />

      <TextField
        label={t('auth.login.password')}
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        error={fieldError('password')}
        trailing={<PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
        {...register('password')}
      />
      <TextField
        label={t('register.confirm_password')}
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        error={fieldError('confirmPassword')}
        {...register('confirmPassword')}
      />

      <label className="flex items-start gap-2.5 text-sm text-ink-soft">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-muted-300 text-primary focus:ring-primary"
          {...register('terms')}
        />
        <span>{t('register.terms')}</span>
      </label>
      {errors.terms && <p className="-mt-2 text-sm text-danger">{t(errors.terms.message ?? '')}</p>}

      <Button type="submit" variant="inverse" size="lg" block loading={register_.isPending}>
        {t('register.submit')}
      </Button>
    </form>
  );
}
